import React, { useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';
import resonanceData from '../data/resonance.json';
import booksData from '../data/books.json';

const SUGGESTIONS = ['Anxiety', 'Leadership', 'Fate', 'Purpose'];

const Explore = () => {
  const [query, setQuery] = useState('');
  const [activePair, setActivePair] = useState(null);

  const isZen = query.trim().length === 0;
  const normalizedQuery = useMemo(
    () => query.trim().toLowerCase().replace(/^#/, ''),
    [query]
  );

  // Build book lookup map (for modal)
  const bookMap = useMemo(() => {
    const map = {};
    booksData.forEach((book) => {
      map[book.id] = book;
    });
    return map;
  }, []);

  const extractUrlFromMarkdownLink = (markdownLink) => {
    if (!markdownLink) return null;
    const raw = String(markdownLink).trim();
    const match = raw.match(/\((https?:\/\/[^)]+)\)/);
    if (match) return match[1];
    if (/^https?:\/\//i.test(raw)) return raw;
    return null;
  };

  // Search scope: question, tags, fusion
  const filteredItems = useMemo(() => {
    if (!normalizedQuery) return [];

    const q = normalizedQuery;
    const contains = (value) => String(value || '').toLowerCase().includes(q);

    return resonanceData.filter((item) => {
      if (contains(item.question)) return true;
      if (contains(item.fusion)) return true;
      if (item.tags && item.tags.length > 0) {
        return item.tags.some((t) =>
          String(t || '').replace(/^#/, '').toLowerCase().includes(q)
        );
      }
      return false;
    });
  }, [normalizedQuery]);

  const handleOpenModal = (pair) => setActivePair(pair);
  const handleCloseModal = () => setActivePair(null);

  const applySuggestion = (text) => {
    setQuery(text);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-dark-slate">
      {/* Zen View: center-only search */}
      <div
        className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-500 ease-out ${
          isZen ? 'min-h-screen flex items-center justify-center py-16' : 'py-20'
        }`}
      >
        <div className={`w-full ${isZen ? 'max-w-3xl' : 'max-w-4xl mx-auto'}`}>
          {/* Header (only when searching) */}
          {!isZen && (
            <div className="text-center mb-10 animate-fadeUp">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-serif text-porcelain-white mb-3">
                Find Your Answer
              </h1>
              <p className="text-base sm:text-lg text-porcelain-white/70 max-w-2xl mx-auto">
                Search through 200+ conversations by keyword or topic.
              </p>
            </div>
          )}

          {/* Main Search Bar */}
          <div
            className={`transition-all duration-500 ease-out ${
              isZen
                ? 'animate-fadeUp'
                : 'animate-fadeUp'
            }`}
          >
            <div className="relative">
              <Search
                size={18}
                className={`absolute left-4 top-1/2 -translate-y-1/2 ${
                  isZen ? 'text-porcelain-white/40' : 'text-porcelain-white/50'
                }`}
              />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search for 'Anxiety', 'Money', 'Purpose'..."
                className="w-full rounded-2xl bg-gray-900/50 border border-white/10 pl-11 pr-11 py-4 text-porcelain-white placeholder:font-serif placeholder:text-porcelain-white/40 focus:outline-none focus:border-white/25 focus:bg-gray-900/60 transition-colors"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full hover:bg-white/10 text-porcelain-white/60 hover:text-porcelain-white transition-colors"
                  aria-label="Clear search"
                >
                  <X size={16} />
                </button>
              )}
            </div>

            {/* Micro guidance (Zen View only) */}
            {isZen && (
              <div className="mt-5 text-sm text-porcelain-white/55">
                <span className="mr-2">Try:</span>
                {SUGGESTIONS.map((s, idx) => (
                  <React.Fragment key={s}>
                    <button
                      type="button"
                      onClick={() => applySuggestion(s)}
                      className="underline underline-offset-4 decoration-white/20 hover:decoration-white/60 hover:text-porcelain-white transition-colors"
                    >
                      {s}
                    </button>
                    {idx < SUGGESTIONS.length - 1 && (
                      <span className="mx-2 text-porcelain-white/30">·</span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>

          {/* Results View */}
          {!isZen && (
            <div className="mt-10 animate-fadeUp">
              {filteredItems.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {filteredItems.map((pair) => (
                    <button
                      key={pair.id}
                      type="button"
                      onClick={() => handleOpenModal(pair)}
                      className="text-left rounded-2xl border border-white/10 bg-gray-950/40 p-5 shadow-xl hover:border-white/20 hover:bg-gray-900/60 transition-colors"
                    >
                      <h3 className="text-gray-100 text-lg font-serif leading-snug mb-4">
                        {pair.question}
                      </h3>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="rounded-xl p-4 border border-red-900/50 bg-gradient-to-br from-[#2c0b0e] to-[#1a0506]">
                          <div className="flex items-baseline justify-between gap-3 mb-3">
                            <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-red-200/80">
                              East
                            </p>
                            <p className="text-[11px] text-red-100/70 font-medium text-right line-clamp-1">
                              {pair.east.author}
                            </p>
                          </div>
                          <blockquote className="text-gray-100 font-serif text-sm leading-relaxed line-clamp-3">
                            {pair.east.text}
                          </blockquote>
                        </div>

                        <div className="rounded-xl p-4 border border-slate-800 bg-gradient-to-br from-[#0f172a] to-[#020617]">
                          <div className="flex items-baseline justify-between gap-3 mb-3">
                            <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-sky-200/80">
                              West
                            </p>
                            <p className="text-[11px] text-sky-100/70 font-medium text-right line-clamp-1">
                              {pair.west.author}
                            </p>
                          </div>
                          <blockquote className="text-gray-100 font-serif text-sm leading-relaxed line-clamp-3">
                            {pair.west.text}
                          </blockquote>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <p className="text-porcelain-white/70 text-lg">
                    No conversations found. Try a different keyword.
                  </p>
                  {query && (
                    <button
                      type="button"
                      onClick={() => setQuery('')}
                      className="mt-5 px-6 py-2 rounded-lg border border-white/15 bg-white/5 text-white hover:bg-white/10 transition-colors"
                    >
                      Clear Search
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Deep Dive Modal (same content model as Resonance) */}
        {activePair && (
          <div className="fixed inset-0 z-40 flex items-center justify-center px-4 sm:px-6 lg:px-8">
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={handleCloseModal}
            />
            <div className="relative z-50 max-w-4xl w-full max-h-[90vh] overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 shadow-2xl">
              {/* Modal Header */}
              <div className="flex items-start justify-between gap-4 px-6 sm:px-8 pt-6 sm:pt-8 pb-4 border-b border-white/10">
                <div>
                  {activePair.category && (
                    <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-white/10 text-white border border-white/20 mb-3">
                      {activePair.category}
                    </div>
                  )}
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-serif text-white leading-snug">
                    {activePair.question}
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="p-2 rounded-full bg-white/5 text-gray-300 hover:bg-white/10 hover:text-white transition-colors"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Modal Body (Scrollable) */}
              <div className="px-6 sm:px-8 py-6 sm:py-8 space-y-8 overflow-y-auto max-h-[calc(90vh-4rem)] pb-24">
                {/* Section 1: Comparison */}
                <section>
                  <h3 className="text-sm font-semibold tracking-[0.16em] uppercase text-gray-400 mb-4">
                    The Comparison
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    {/* East */}
                    <div className="rounded-2xl p-5 border border-red-900/60 bg-gradient-to-br from-[#2c0b0e] via-[#1a0506] to-black">
                      <div className="flex items-baseline justify-between gap-3 mb-3">
                        <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-red-200/80">
                          East
                        </p>
                        <p className="text-xs text-red-100/80 font-medium text-right">
                          {activePair.east.author}
                        </p>
                      </div>
                      <blockquote className="text-gray-50 font-serif text-base leading-relaxed mb-3">
                        “{activePair.east.text}”
                      </blockquote>
                      {activePair.east.meaning && (
                        <p className="text-sm text-red-100/90 leading-relaxed">
                          {activePair.east.meaning}
                        </p>
                      )}
                    </div>

                    {/* West */}
                    <div className="rounded-2xl p-5 border border-slate-700 bg-gradient-to-br from-slate-900 via-slate-950 to-black">
                      <div className="flex items-baseline justify-between gap-3 mb-3">
                        <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-sky-200/80">
                          West
                        </p>
                        <p className="text-xs text-sky-100/80 font-medium text-right">
                          {activePair.west.author}
                        </p>
                      </div>
                      <blockquote className="text-gray-50 font-serif text-base leading-relaxed mb-3">
                        “{activePair.west.text}”
                      </blockquote>
                      {activePair.west.meaning && (
                        <p className="text-sm text-sky-100/90 leading-relaxed">
                          {activePair.west.meaning}
                        </p>
                      )}
                    </div>
                  </div>
                </section>

                {/* Section 2: Fusion */}
                {activePair.fusion && (
                  <section>
                    <h3 className="text-sm font-semibold tracking-[0.16em] uppercase text-gray-400 mb-3">
                      The Fusion
                    </h3>
                    <div className="rounded-2xl border border-amber-200/30 bg-gradient-to-r from-amber-500/10 via-pink-500/10 to-sky-500/10 shadow-[0_0_40px_rgba(251,191,36,0.18)]">
                      <div className="p-5 sm:p-6">
                        <p className="text-base sm:text-lg text-amber-50 leading-relaxed font-serif">
                          {activePair.fusion}
                        </p>
                      </div>
                    </div>
                  </section>
                )}

                {/* Section 3: Practical Application */}
                {(activePair.real_life || activePair.try_this) && (
                  <section>
                    <h3 className="text-sm font-semibold tracking-[0.16em] uppercase text-gray-400 mb-4">
                      Practical Application
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                      {activePair.real_life && (
                        <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                          <p className="text-xs font-semibold tracking-[0.16em] uppercase text-gray-300 mb-2">
                            Real-life Example
                          </p>
                          <p className="text-sm text-gray-100 leading-relaxed">
                            {activePair.real_life}
                          </p>
                        </div>
                      )}

                      {activePair.try_this && (
                        <div className="rounded-2xl border border-emerald-300/60 bg-gradient-to-br from-emerald-500/10 via-emerald-400/10 to-emerald-500/5 p-5 shadow-[0_0_32px_rgba(16,185,129,0.25)]">
                          <p className="text-xs font-semibold tracking-[0.16em] uppercase text-emerald-100 mb-2">
                            Try This
                          </p>
                          <p className="text-sm sm:text-base text-emerald-50 leading-relaxed font-medium">
                            {activePair.try_this}
                          </p>
                        </div>
                      )}
                    </div>
                  </section>
                )}

                {/* Footer: Further Reading */}
                {activePair.books && activePair.books.length > 0 && (
                  <section className="mt-8">
                    <h3 className="text-xs font-mono text-gray-500 tracking-widest uppercase mb-3">
                      Further Reading
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {activePair.books.map((bookId) => {
                        const bookDetails =
                          booksData.find((b) => b.id === bookId) || bookMap[bookId];
                        if (!bookDetails) return null;
                        const url = extractUrlFromMarkdownLink(bookDetails.amazon_link);

                        return (
                          <a
                            key={bookId}
                            href={url || '#'}
                            target={url ? '_blank' : undefined}
                            rel={url ? 'noopener noreferrer' : undefined}
                            className="group flex items-center gap-3 rounded-lg border border-white/10 bg-white/5 p-3 md:p-4 hover:bg-white/10 hover:border-white/20 transition-all cursor-pointer"
                          >
                            {bookDetails.cover_image ? (
                              <img
                                src={bookDetails.cover_image}
                                alt={bookDetails.title}
                                className="w-10 h-14 object-cover rounded border border-white/10 flex-shrink-0"
                              />
                            ) : (
                              <div className="w-10 h-14 rounded bg-black/40 border border-white/10 overflow-hidden flex items-center justify-center text-[10px] text-gray-300 uppercase tracking-wide flex-shrink-0">
                                {bookDetails.title.split('(')[0].trim()}
                              </div>
                            )}
                            <div className="flex flex-col min-w-0 flex-1">
                              <p className="font-serif font-bold text-gray-200 group-hover:text-amber-200 line-clamp-1">
                                {bookDetails.title}
                              </p>
                              {bookDetails.one_liner && (
                                <p className="text-xs text-gray-500 line-clamp-1 mt-1">
                                  {bookDetails.one_liner}
                                </p>
                              )}
                            </div>
                          </a>
                        );
                      })}
                    </div>
                  </section>
                )}
              </div>
            </div>
          </div>
        )}

        <style>{`
          @keyframes fadeUp {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fadeUp {
            animation: fadeUp 0.35s ease-out;
          }
        `}</style>
      </div>
    </div>
  );
};

export default Explore;
